import { Tooltip } from '@mui/material';
import i18next from 'i18next';
import React from 'react';

import { getTheme } from '@commons/utils/theme';
import DisplayNumber from '@commons/DisplayNumber';

import {
  CenteredFlexBox,
  HeaderTooltipBox,
  HeaderTypography,
  InlineFlexTypography,
  TooltipIcon,
  TooltipTypography,
  ExpandIcon,
  TreeCellTypography,
} from './styledComponents';

// Render header with tooltip
const renderHeaderWithTooltip = (headerName, tooltipText) => {
  if (!tooltipText) {
    return headerName;
  }

  const theme = getTheme();

  return (
    <HeaderTooltipBox>
      <HeaderTypography>{headerName}</HeaderTypography>
      <Tooltip
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.colors.greys.darkest,
              opacity: 1,
              padding: '12px',
            },
          },
        }}
        placement="top"
        title={<TooltipTypography $theme={theme}>{tooltipText}</TooltipTypography>}
        arrow
      >
        <TooltipIcon alt="info" src="/images/inpulse/info-black-small.svg" />
      </Tooltip>
    </HeaderTooltipBox>
  );
};

export const getColumns = () => [
  {
    field: 'unit',
    headerName: i18next.t('GENERAL.SOLD_QUANTITY'),
    flex: 1,
    minWidth: 100,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => <DisplayNumber number={params.value} withoutDecimals />,
  },
  {
    field: 'priceExcludingTaxes',
    headerName: i18next.t('GENERAL.AVERAGE_PRICE_EX_TAX'),
    flex: 1,
    minWidth: 100,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderHeader: () =>
      renderHeaderWithTooltip(
        i18next.t('GENERAL.AVERAGE_PRICE_EX_TAX'),
        i18next.t('ANALYSIS.SALES.COLUMN_TOOLTIP_PAST_PRODUCT_MIX_PRICE_WITHOUT_TAXES'),
      ),
    renderCell: (params) => <DisplayNumber number={params.value} displayCurrencyCode />,
  },
  {
    field: 'turnoverExcludingTaxes',
    headerName: i18next.t('GENERAL.TURNOVER_EX_TAX'),
    flex: 1,
    minWidth: 100,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderHeader: () =>
      renderHeaderWithTooltip(
        i18next.t('GENERAL.TURNOVER_EX_TAX'),
        i18next.t('ANALYSIS.SALES.COLUMN_TOOLTIP_REVENUE_WITHOUT_TAXES'),
      ),
    renderCell: (params) => <DisplayNumber number={params.value} displayCurrencyCode />,
  },
  {
    field: 'mixExcludingTaxes',
    headerName: i18next.t('ANALYSIS.SALES.COLUMN_NAME_PERCENT_PRODUCT_MIX'),
    flex: 1,
    minWidth: 100,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderHeader: () =>
      renderHeaderWithTooltip(
        i18next.t('ANALYSIS.SALES.COLUMN_NAME_PERCENT_PRODUCT_MIX'),
        i18next.t('ANALYSIS.SALES.COLUMN_TOOLTIP_PERCENT_PRODUCT_MIX_WITHOUT_TAXES'),
      ),
    renderCell: (params) => (
      <InlineFlexTypography>
        <DisplayNumber number={params.value} />%
      </InlineFlexTypography>
    ),
  },
  {
    field: 'marginRatio',
    headerName: i18next.t('ANALYSIS.SALES.COLUMN_NAME_PERCENT_MARGIN'),
    flex: 1,
    minWidth: 100,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderHeader: () =>
      renderHeaderWithTooltip(
        i18next.t('ANALYSIS.SALES.COLUMN_NAME_PERCENT_MARGIN'),
        i18next.t('ANALYSIS.SALES.COLUMN_TOOLTIP_PERCENT_MARGIN_WITHOUT_TAXES'),
      ),
    renderCell: (params) => (
      <InlineFlexTypography>
        {params.value === 0 ? (
          '-'
        ) : (
          <>
            <DisplayNumber number={params.value} />%
          </>
        )}
      </InlineFlexTypography>
    ),
  },
  {
    field: 'margin',
    headerName: i18next.t('ANALYSIS.SALES.COLUMN_NAME_MARGIN_WITHOUT_TAXES'),
    flex: 1,
    minWidth: 100,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderHeader: () =>
      renderHeaderWithTooltip(
        i18next.t('ANALYSIS.SALES.COLUMN_NAME_MARGIN_WITHOUT_TAXES'),
        i18next.t('ANALYSIS.SALES.COLUMN_TOOLTIP_MARGIN_WITHOUT_TAXES'),
      ),
    renderCell: (params) =>
      params.value === 0 ? '-' : <DisplayNumber number={params.value} displayCurrencyCode />,
  },
  {
    field: 'indicator',
    headerName: '',
    width: 50,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const rowHasChildren = params.row.hasChildren;
      if (!rowHasChildren) {
        return null;
      }

      // Get expansion state directly from DataGrid API
      const rowNode = params.api.getRowNode(params.id);
      const isExpanded = rowNode?.childrenExpanded ?? false;
      return (
        <CenteredFlexBox>
          <ExpandIcon
            $isExpanded={isExpanded}
            alt={isExpanded ? 'expanded' : 'collapsed'}
            src="/images/inpulse/carret-black.svg"
          />
        </CenteredFlexBox>
      );
    },
  },
];

export const getGroupingColumn = () => ({
  field: 'name',
  headerName: i18next.t('GENERAL.NAME'),
  flex: 2,
  minWidth: 200,
  hideDescendantCount: true,
  align: 'left',
  headerAlign: 'left',
  sortable: true,
  sortingOrder: ['asc', 'desc', null],
  renderCell: (params) => {
    const depth = params.row.path ? params.row.path.length - 1 : 0;
    const paddingLeft = depth === 0 ? '0px' : depth === 1 ? '16px' : '32px';
    const productId = params.row.productId;
    const hasLink = !!productId;

    const handleNameClick = (e) => {
      if (hasLink) {
        e.stopPropagation();
        window.open(`/admin/products/${productId}/details`, '_blank');
      }
    };

    return (
      <TreeCellTypography
        $paddingLeft={paddingLeft}
        style={{
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          ...(hasLink ? { cursor: 'pointer', textDecoration: 'underline' } : {}),
        }}
        onClick={hasLink ? handleNameClick : undefined}
      >
        {params.row.name.toUpperCase()}
      </TreeCellTypography>
    );
  },
});
